import React from "react";

import { useAuth } from "@wc/domain/authn";

import { loadOrganization } from "../core/org-access";
import { updateOrganization } from "../core/org-update";

/**
 *
 * @param {object} org The organization or null
 * @param {(org: object|null) => void} setOrg To set the organization
 * @param {boolean} loading Whether the org is loading or not
 * @param {(loading: boolean) => void} _setLoading Set the org to not load anymore
 * @param {object} authData The auth context data
 * @returns {object} The org context data
 */
function createOrgContextData(org, setOrg, loading, _setLoading, authData) {
  return {
    org,
    isLoading: loading,
    updateOrgDisplayName: async (newName) => {
      if (newName.length === 0) {
        throw new Error("Unable to set an empty name");
      }
      const oldName = org.displayName;

      const newOrg = { ...org };
      newOrg.displayName = newName;
      setOrg(newOrg);
      try {
        await updateOrganization(newOrg, authData.getCurrentUser());
      } catch (error) {
        console.error(error);
        const backToOldName = { ...org };
        backToOldName.displayName = oldName;
        setOrg(backToOldName);
      }
    },
  };
}

export const OrgContext = React.createContext(null);

/**
 * @param {object} props The provider props
 * @param {string} [props.orgId] The ID of the organization (used for lookup)
 * @param {object} [props.initialOrg] Pre-loaded org object (skips lookup if provided)
 * @param {string} props.userId The id of the user looking for this
 * @param {React.ReactElement} props.children The children to render
 * @returns {React.ReactElement} The organization provider
 */
export default function OrgProvider({ orgId, initialOrg, userId, children }) {
  const authData = useAuth();
  const [org, setOrg] = React.useState(initialOrg || null);
  const [currentOrgId, setCurrentOrgId] = React.useState(
    initialOrg?.id || null,
  );
  const [loading, setLoading] = React.useState(!initialOrg);

  React.useEffect(() => {
    // Skip fetch if we have an initialOrg that matches
    if (initialOrg && initialOrg.id === orgId) {
      if (org?.id !== initialOrg.id) {
        setOrg(initialOrg);
        setCurrentOrgId(initialOrg.id);
        setLoading(false);
      }
      return;
    }

    const fetchOrg = async () => {
      setLoading(true);
      try {
        const loadedOrg = await loadOrganization(orgId, userId);
        setOrg(loadedOrg);
        setCurrentOrgId(orgId);
      } catch (error) {
        console.error(`Could not load org ${orgId}`, error);
        setOrg(null);
      } finally {
        setLoading(false);
      }
    };

    // Fetch if no org loaded or if orgId changed
    if (orgId && (!org || orgId !== currentOrgId)) {
      fetchOrg();
    }
  }, [orgId, userId, org, currentOrgId, initialOrg]);

  const contextData = createOrgContextData(
    org,
    setOrg,
    loading,
    setLoading,
    authData,
  );

  return (
    <OrgContext.Provider value={contextData}>{children}</OrgContext.Provider>
  );
}
